import { proxyCustomElement, HTMLElement, createEvent, h } from '@stencil/core/internal/client';
import { S as StringService } from './index2.js';
import { D as DomService, K as Key } from './index3.js';
import { T as TranslationService } from './index6.js';
import { V as ValidationService } from './index9.js';
import { d as defineCustomElement$2 } from './roche-icon2.js';
import { d as defineCustomElement$1 } from './roche-tooltip2.js';

var InputType;
(function (InputType) {
    InputType["TEXT"] = "text";
    InputType["TEL"] = "tel";
    InputType["EMAIL"] = "email";
    InputType["PASSWORD"] = "password";
    InputType["HIDDEN"] = "hidden";
    InputType["TEXTAREA"] = "textarea";
})(InputType || (InputType = {}));
const inputTypeKeys = Object.keys(InputType);
inputTypeKeys.map((type) => InputType[type]);

const rocheInputCss = ":host{display:block;margin-top:3rem;margin-top:2rem}@media (min-width: 90rem){:host{margin-top:4.5rem}}:host,:host *{box-sizing:border-box}@media (min-width: 90rem){:host{margin-top:2rem}}:host .label{font-family:\"roche-sans\";font-style:normal;font-weight:200;font-weight:400;font-size:var(--label--font-size--small-up);line-height:1.3125rem;display:flex;justify-content:space-between;margin-bottom:0.5rem;color:#544f4f}@media (min-width: 48rem){:host .label{font-size:var(--label--font-size--medium-up);line-height:1.3125rem}}:host .label-wrapper{display:flex;position:relative}:host .label-wrapper .hint{font-family:\"roche-sans\";font-style:normal;font-weight:200;font-weight:400;font-size:var(--label--font-size--small-up);line-height:1.3125rem;color:#544f4f;margin-left:auto}@media (min-width: 48rem){:host .label-wrapper .hint{font-size:var(--label--font-size--medium-up);line-height:1.3125rem}}:host .error{font-family:\"roche-sans\";font-style:normal;font-weight:200;font-weight:400;font-size:var(--label--font-size--small-up);line-height:1.3125rem;margin-top:0.25rem;color:var(--color-status--error)}@media (min-width: 48rem){:host .error{font-size:var(--label--font-size--medium-up);line-height:1.3125rem}}:host .input{font-family:\"roche-sans\";font-style:normal;font-weight:200;font-size:var(--body-small--font-size--small-up);line-height:1.625rem;display:flex;min-width:100%;max-width:100%;border:1px solid #c2bab5;border-bottom:1px solid #706b69;border-radius:0;padding:0.75rem 1.0625rem;margin:0;background-color:transparent;color:var(--color-text--primary)}@media (min-width: 48rem){:host .input{font-size:var(--body-small--font-size--medium-up);line-height:1.8125rem}}:host .input:hover{border-bottom-color:var(--color-interactive--primary-idle)}:host .input:active,:host .input:focus{border-color:var(--color-interactive--primary-idle);outline:none}:host .input::placeholder{color:#706b69}:host .element-container--disabled .input-container{position:relative;cursor:not-allowed}:host .element-container--disabled .input-container::before{content:\"\";position:absolute;top:0;left:0;width:100%;height:100%;opacity:0.05;background-color:var(--color-interactive--disabled)}:host .element-container--disabled .label,:host .element-container--disabled .hint{color:var(--color-interactive--disabled)}:host .element-container--disabled .input{border-color:transparent;color:var(--color-interactive--disabled)}:host .element-container--error .input{border-color:var(--color-status--error)}:host .element-container--error .input:hover{border-bottom-color:var(--color-status--error)}:host .element-container--error .input:active,:host .element-container--error .input:focus{border-color:var(--color-status--error)}:host roche-tooltip{margin:0 0 0 0.625rem}:host([type=hidden]){display:none}:host([dir=rtl]) .label-wrapper .hint{flex-grow:1;text-align:left}";
const RocheInputStyle0 = rocheInputCss;

const validators = {
    [InputType.EMAIL]: (text) => (ValidationService.isEmail(text)),
    // eslint-disable-next-line max-len
    [InputType.TEXT]: (text, pattern) => (pattern ? ValidationService.isTextValid(text, pattern) : !!text),
};
const validateValue = (input, type, validationPattern = '') => {
    const validator = validators[type];
    if (validator) {
        return validator(input, validationPattern);
    }
    return !!input;
};
const RocheInput = /*@__PURE__*/ proxyCustomElement(class RocheInput extends HTMLElement {
    // TODO: remove comments after the PR is approved.
    get valid() {
        // Check if the field is not required and the value is empty, then consider it valid.
        if (!this.required && !this.value) {
            return true;
        }
        // For all other cases, perform validation based on the type and validationPattern
        // This covers:
        // - if the field is required (regardless of whether a value is present or not)
        // - if the field is not required but a value is present
        return validateValue(this.value, this.type, this.validationPattern);
    }
    get hintParsed() {
        const firstPass = this.hint.replace(/\$0/g, `${(this.value || '').length}`);
        return !this.maxLength
            ? firstPass
            : firstPass.replace(/\$1/g, `${this.maxLength - (this.value || '').length}`);
    }
    get inputStylesParsed() {
        return typeof this.inputStyles === 'string' ? JSON.parse(this.inputStyles) : this.inputStyles;
    }
    handleRocheFormControlInit() {
        this.formControlChangeEvent.emit(this);
    }
    handleRequiredChanged() {
        this.changeEvent.emit(this.value);
        this.formControlChangeEvent.emit(this);
    }
    constructor() {
        super();
        this.__registerHost();
        this.__attachShadow();
        this.formControlChangeEvent = createEvent(this, "rocheFormControlChange", 7);
        this.changeEvent = createEvent(this, "rocheInputChange", 7);
        this.submitEvent = createEvent(this, "rocheInputSubmit", 7);
        this.uid = undefined;
        this.name = undefined;
        this.label = undefined;
        this.value = undefined;
        this.placeholder = undefined;
        this.required = false;
        this.tooltip = undefined;
        this.type = InputType.TEXT;
        this.validationPattern = undefined;
        this.maxLength = undefined;
        this.hint = undefined;
        this.error = undefined;
        this.disabled = false;
        this.inputStyles = undefined;
        this.translations = undefined;
        this.dirty = false;
        this.handleKeyUp = this.handleKeyUp.bind(this);
        this.handleChange = this.handleChange.bind(this);
        this.handleBlur = this.handleBlur.bind(this);
        this.handleRequiredChanged = this.handleRequiredChanged.bind(this);
    }
    async componentWillLoad() {
        this.translations = {
            optional: await TranslationService.translate('optional'),
        };
    }
    componentDidLoad() {
        if (this.value === undefined) {
            this.value = this.inputElement.value;
        }
        else {
            this.inputElement.value = this.value;
        }
        DomService.handleTextDirection(this.element);
    }
    render() {
        const hasError = this.dirty && !this.valid;
        const inputClasses = {
            'element-container': true,
            'element-container--error': hasError,
            'element-container--disabled': this.disabled,
        };
        const isRenderLabel = !!(this.label || this.required || this.hint);
        return (h("div", { key: 'b7477602c6efe832a912557f88a8e028fd6bc1ce', class: inputClasses }, isRenderLabel && h("div", { class: "label-wrapper" }, h("label", { class: "label" }, h("span", null, this.label, !this.required ? ` (${StringService.capitalize(this.translations.optional)})` : '')), this.tooltip
            && h("roche-tooltip", null, this.tooltip), this.hint && (h("span", { class: "hint" }, this.hintParsed))), h("div", { key: '1c2cdfe444e21cfcfd121d345b9dfd126330c4ae', class: "input-container" }, this.renderInput()), h("div", { key: 'db032a62aa04157238d69a7f78000d57fe022522', class: "error" }, hasError && !this.disabled && this.error ? this.error : '')));
    }
    renderInput() {
        return this.type !== InputType.TEXTAREA ? (h("input", { style: this.inputStylesParsed, class: "input", type: this.type, maxLength: this.maxLength || undefined, placeholder: this.placeholder, disabled: this.disabled, onKeyDown: this.handleChange, onKeyUp: this.handleKeyUp, onBlur: this.handleBlur, ref: (element) => { this.inputElement = element; } })) : (h("textarea", { class: "input", rows: 5, maxLength: this.maxLength || undefined, placeholder: this.placeholder, onKeyDown: this.handleChange, onKeyUp: this.handleKeyUp, onBlur: this.handleBlur, ref: (element) => {
                this.inputElement = element;
            } }));
    }
    handleKeyUp({ keyCode }) {
        this.handleChange();
        if (keyCode === Key.ENTER) {
            this.submitEvent.emit(this.value);
        }
    }
    handleChange() {
        if (this.inputElement.value !== this.value) {
            this.value = this.inputElement.value;
            this.changeEvent.emit(this.value);
            this.formControlChangeEvent.emit(this);
        }
    }
    handleBlur() {
        this.dirty = true;
    }
    get element() { return this; }
    static get watchers() { return {
        "required": ["handleRequiredChanged"]
    }; }
    static get style() { return RocheInputStyle0; }
}, [1, "roche-input", {
        "uid": [1],
        "name": [1],
        "label": [1],
        "value": [1025],
        "placeholder": [1],
        "required": [4],
        "tooltip": [1],
        "type": [513],
        "validationPattern": [1, "validation-pattern"],
        "maxLength": [2, "max-length"],
        "hint": [1],
        "error": [1],
        "disabled": [4],
        "inputStyles": [1, "input-styles"],
        "translations": [32],
        "dirty": [32]
    }, [[0, "rocheFormControlInit", "handleRocheFormControlInit"]], {
        "required": ["handleRequiredChanged"]
    }]);
function defineCustomElement() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["roche-input", "roche-icon", "roche-tooltip"];
    components.forEach(tagName => { switch (tagName) {
        case "roche-input":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, RocheInput);
            }
            break;
        case "roche-icon":
            if (!customElements.get(tagName)) {
                defineCustomElement$2();
            }
            break;
        case "roche-tooltip":
            if (!customElements.get(tagName)) {
                defineCustomElement$1();
            }
            break;
    } });
}

export { InputType as I, RocheInput as R, defineCustomElement as d };

//# sourceMappingURL=roche-input2.js.map